import { jsPDF } from "jspdf";
import type {
  LessonData,
  SlideData,
  ContentElement,
  ContentSlideData,
  QuizSlideData,
  SpeakSlideData,
} from "@/types/lesson";
import { INSTITUTE_NAME } from "@/lib/branding";
import { stepItemText } from "@/lib/script";

const MAX_BULLET = 200;
const MAX_NARRATION = 220;

function truncate(s: string, max: number): string {
  const t = s.replace(/\s+/g, " ").trim();
  if (!t) return "";
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1).trim()}...`;
}

function elementOverviewLine(el: ContentElement): string {
  switch (el.type) {
    case "text":
      return truncate(el.content || "", MAX_BULLET);
    case "term":
      return `Key term: "${el.term}" — ${truncate(el.definition || "", 140)}`;
    case "example":
      return truncate(
        [el.label, el.content].filter(Boolean).join(" — ") || "",
        MAX_BULLET
      );
    case "tip":
      return `Tip: ${truncate(el.text || "", MAX_BULLET)}`;
    case "steps": {
      const items = (el.items || []).map((it) => stepItemText(it)).filter(Boolean);
      const head = el.title ? `${el.title}: ` : "Steps: ";
      return truncate(head + items.slice(0, 4).join(" · "), MAX_BULLET);
    }
    case "bar_chart":
    case "line_chart":
      return truncate([el.title, ...(el.labels || [])].filter(Boolean).join(" · "), MAX_BULLET);
    case "pie_chart":
      return truncate([el.title, ...(el.labels || [])].filter(Boolean).join(" · "), MAX_BULLET);
    case "comparison_table":
      return truncate(
        [el.title, ...(el.headers || []), ...(el.rows || []).flat()].filter(Boolean).join(" · "),
        MAX_BULLET
      );
    case "process_flow":
      return truncate(
        [el.title, ...(el.steps || []).map((s) => s.label)].filter(Boolean).join(" → "),
        MAX_BULLET
      );
    case "labeled_diagram":
      return truncate(
        [el.title, el.description, ...(el.parts || []).map((p) => p.label)].filter(Boolean).join(" · "),
        MAX_BULLET
      );
    default:
      return "";
  }
}

function bulletsForContentSlide(s: ContentSlideData): string[] {
  const out: string[] = [];
  if (s.narration) {
    out.push(truncate(s.narration, MAX_NARRATION));
  }
  if (s.elements?.length) {
    for (const el of s.elements) {
      const line = elementOverviewLine(el);
      if (line) out.push(line);
    }
  }
  if (out.length === 0 && s.title) {
    out.push(`Content: ${s.title}`);
  }
  return out.slice(0, 8);
}

function bulletsForQuiz(s: QuizSlideData): string[] {
  const out: string[] = [];
  if (s.narration) out.push(truncate(s.narration, 160));
  out.push(`Question: ${truncate(s.question, MAX_BULLET)}`);
  out.push("Activity: multiple-choice check (options shown in the lesson).");
  return out;
}

function bulletsForSpeak(s: SpeakSlideData): string[] {
  const out: string[] = [];
  if (s.narration) out.push(truncate(s.narration, 160));
  out.push(`Task: ${truncate(s.prompt, 180)}`);
  out.push(`Practice phrase: "${truncate(s.phrase, 120)}"`);
  return out;
}

function slideTypeLabel(slide: SlideData): string {
  if (slide.type === "content") return "Content";
  if (slide.type === "quiz") return "Quiz";
  return "Speaking practice";
}

function bulletsForSlide(slide: SlideData): string[] {
  if (slide.type === "content") return bulletsForContentSlide(slide);
  if (slide.type === "quiz") return bulletsForQuiz(slide);
  return bulletsForSpeak(slide);
}

function safeFilename(title: string): string {
  return (
    title
      .replace(/[^\w\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .slice(0, 60) || "lesson"
  );
}

/**
 * Builds a slide-dependent overview (not full transcript) for revision handouts.
 */
export function downloadLessonSummaryPdf(lesson: LessonData): void {
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 18;
  const maxW = pageW - margin * 2;
  let y = margin;
  const bodyLine = 6;
  const sectionGap = 4;

  const checkPage = (extra: number) => {
    if (y + extra > pageH - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const addLines = (text: string, fontSize: number, weight: "normal" | "bold") => {
    doc.setFont("helvetica", weight);
    doc.setFontSize(fontSize);
    const lines = doc.splitTextToSize(text, maxW);
    for (const line of lines) {
      checkPage(bodyLine + 2);
      doc.text(line, margin, y);
      y += fontSize <= 11 ? bodyLine : bodyLine + 1;
    }
  };

  // Title block
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  addLines("Lesson overview", 16, "bold");
  y += 2;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  addLines(INSTITUTE_NAME, 10, "normal");
  y += sectionGap;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  addLines(lesson.lesson_title || "Lesson", 13, "bold");
  y += sectionGap;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const dateStr = new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  addLines(`Generated ${dateStr} · This summary follows your lesson slides at a glance.`, 9, "normal");
  y += sectionGap + 2;

  lesson.slides.forEach((slide, i) => {
    checkPage(40);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    const header = `Slide ${i + 1} — ${slide.title} (${slideTypeLabel(slide)})`;
    const headerLines = doc.splitTextToSize(header, maxW);
    for (const line of headerLines) {
      checkPage(bodyLine + 2);
      doc.text(line, margin, y);
      y += bodyLine + 0.5;
    }
    y += 1;

    const bullets = bulletsForSlide(slide);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    for (const b of bullets) {
      const prefixed = `• ${b}`;
      const blines = doc.splitTextToSize(prefixed, maxW - 4);
      for (const line of blines) {
        checkPage(bodyLine + 2);
        doc.text(line, margin + 2, y);
        y += bodyLine;
      }
    }
    y += sectionGap + 2;
  });

  doc.save(`${safeFilename(lesson.lesson_title)}-lesson-summary.pdf`);
}
